<template>
  <div name="evidence" class="mr-10">
    <div class="ml-6">
      <header class="flex justify-between mt-4 mb-3">
        <div class="pl-1">
          <h1 class="font-semibold text-base">{{ pageTitle }}</h1>
          <p class="text-xs">{{ description }}</p>
        </div>

        <div class="flex">
          <div class="mr-1" v-if="selectedEvidences.length > 0">
            <button
              type="button"
              @click="showMove = true"
              class="
                filter-bar-button 
                not-round
                hover:opacity-75
                focus:outline-none
              "
              title="Move Evidence"
            >
              <i class="fas fa-compress mr-1"></i> Move evidence
            </button>
          </div>

          <div>
            <input
              type="search"
              class="filter-bar-search focus:outline-none w-56"
              name="search"
              v-model="search"
              @input="lazyCaller"
              placeholder="Search tags..."
            />
            <i
              class="
                fas
                fa-times
                text-sm
                absolute
                -ml-8
                mt-4
                cursor-pointer
                hover:opacity-50
              "
              @click="clearTags()"
              title="Clear search"
              v-show="search.length > 0"
            ></i>
          </div>

          <div class="mr-1">
            <button
              @click="toggleSelect()"
              type="button"
              class="filter-bar-button hover:opacity-75 focus:outline-none"
              v-bind:class="inSelectMode ? 'bg-gray-400' : 'bg-white'"
            >
              <i class="fas fa-object-group"></i>
              Select
            </button>
          </div>

          <div class="mr-1">
            <button
              type="button"
              @click="showAdditionWindow = true"
              class="
                action-button
                not-round
                bg-blue-500
                hover:bg-gray-400 
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              title="Add Evidence"
               :disabled="!hasAccess"
            >
              <i class="fas fa-plus mr-1"></i> Add evidence
            </button>
          </div>

        </div>
      </header>


      <transition name="fade">
        <div class="loading" v-show="loading">
          <span class="fa fa-spinner fa-spin"></span> Loading
        </div>
      </transition>

      <div v-if="errored" class="empty">
        <p>
          We are not able to retrieve this information at the moment, please try
          back later
        </p>
      </div>

      <div v-if="hasAccess">

      <div
        class="
          px-auto
          grid grid-cols-8
          lg:grid-cols-8
          gap-2 gap-y-2
          bg-white
          pb-6
          mt-4
        "
        v-if="componentLoaded"
      >
        <div
          class="relative"
          v-for="item in filteredEvidences"
          :key="item.identifier"
        >
          <div class="absolute w-full">
            <i
              v-if="hasNotes(item)"
              class="fas fa-comment float-right text-white p-2 text-2xl"
            ></i>
          </div>

          <div  @click="selectEvidence(item)"
          class="max-w-sm rounded overflow-hidden shadow-xl cursor-pointer "
          v-bind:class="inSelectMode ? 'hover:opacity-100' : 'hover:opacity-50'" >
            <evidencevideo
              v-if="item.media_type === 'video'"
              reference="'ref_'+index"
              :url="loadFile(item.media_url)"
              width="100%"
              height="100%"
              class="overflow-hidden"
              style="max-height: 110px !important"
            ></evidencevideo>
            <evidenceimage
              v-else
              :url="loadFile(item.media_url)"
              reference="'ref_'+index"
              class="overflow-hidden"
              style="max-height: 110px !important"
            ></evidenceimage>
            <div class="px-4 p-2 text-xs">
              <div>
                {{ getLessonName(item) }}
              </div>
              <div class="text-xs text-orange-400" v-html="getClassName(item)"></div>
            </div>

            <div v-if="inSelectMode"
            class="absolute top-0 w-full h-full bg-red-100 bg-opacity-50 text-center">
              <i
                class="
                  inline-block
                  font-bold
                  w-2/12
                  mx-auto
                  mt-6
                  rounded-full
                  icon
                  text-3xl
                  hover:text-gray-600
                "
                v-bind:class="
                  evidenceIsSelected(item)
                    ? 'text-red-400 fas fa-check-circle opacity-100'
                    : 'text-gray-100 far fa-check-circle opacity-25'
                "
              ></i>
       
            </div>
          </div>
        </div>
      </div>
      <p class="ml-5 mt-6 text-sm" v-else>
        We could not find any data for your given selection.
      </p>

      <div class="w-full mb-8" v-if="componentLoaded">
        <Pagination
          :total-pages="totalPages"
          :total="totalResultsCount"
          :current-page="currentPage"
          :has-more-pages="hasMorePages"
          :pageSize="pageSize"
          @pagechanged="showMore"
        >
        </Pagination>
      </div>
    </div>

      <div v-else>
        <UpgradeContent :area="'Lesson Evidence'"></UpgradeContent>
      </div>


        <!--------- ADD ------------>

      <ModalSlide
        :show="showAdditionWindow"
        :cssWidth="'md:w-6/12'"
        @closeModal="closeAddition"
      >
        <template v-slot:header-icon>
          <i class="fas fa-plus"></i>
        </template>
        <template v-slot:header-title>
          Add New Evidence
          <div class="text-xs">{{ descriptionAddEvidence }}</div>
        </template>
        <template v-slot:body>
          <evidenceaddition
            :lessons="orderedLessons"
            :classPreset="selectedClass"
            :lessonPreset="lesson"
            :pupils="pupils"
            @evidence-added="evidenceAdded"
            v-if="showAdditionWindow"
          >
          </evidenceaddition>
        </template>
      </ModalSlide>


        <!--------- MOVE ------------>

        <ModalSlide :show="showMove" @closeModal="closeMoveModal">
      <template v-slot:header-icon>
        <i class="fas fa-compress"></i>
      </template>
      <template v-slot:header-title>
        Move Evidence
        <div class="text-xs">Move {{ selectedEvidences.length }} items of evidence between lessons and classes.</div>
      </template>
      <template v-slot:body>
        <EvidenceMove :lessons="orderedLessons" :evidences="selectedEvidences" @evidence-moved="evidenceMoved" v-if="selectedEvidences.length > 0 && showMove"></EvidenceMove>
      </template>
    </ModalSlide>

    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from "vuex";
import { EvidenceService } from "@/services/evidence.service";

import { fileEvidenceMixin } from "@/mixins/file-EvidenceMixin.js";

const UpgradeContent = () => import("@/components/upgrade/UpgradeContent.vue");

const Pagination = () => import("@/components/helpers/Pagination.vue");
const ModalSlide = () => import("@/components/ModalSlide.vue");

const evidenceimage = () => import("@/components/evidence/EvidenceImage.vue");
const evidencevideo = () => import("@/components/evidence/EvidenceVideo.vue");
const EvidenceMove = () => import("@/components/evidence/TheEvidenceMove.vue");
const evidenceaddition = () => import("@/components/evidence/TheEvidenceAdd.vue");

export default {
  name: "evidence",
  mixins: [fileEvidenceMixin],
  props: {
    course: Object,
    lessons: Array,
    pupils: Array,
    classes: Array,
    selectedClass: Object,
    lesson: Object,
  },
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
    }),
    componentLoaded() {
      return this.filteredEvidences.length > 0;
    },
      hasAccess() {
        if (this.currentUser.isOnFree || this.currentUser.isOnLite ) {
            return this.currentAccess.evidenceAccess
       } else {
        return true
       }
    },
    pageTitle() {
      return this.lesson != undefined
        ? this.lesson.name + " Evidence"
        : "Unit Evidence";
    },
    description() {
      if (this.componentLoaded) {
        return "Unit and lesson evidence results.";
      }
    },
    descriptionAddEvidence() {
      if (this.hasValidLessonSelected) {
        return "Upload evidence @ " + this.lesson.name;
      } else {
        return "Upload evidence against a selected lesson";
      }
    },
    descriptionViewEvidence() {
      return "Last edited @ " + moment(this.evidenceAsset.modified_date).format('Do MMMM YYYY, h:mm a');
    },
    hasValidLessonSelected() {
      return this.lesson != undefined;
    },
    hasMorePages() {
      return this.pagination.has_more === true;
    },
    totalResultsCount() {
      return this.pagination.total_count;
    },
    totalPages() {
      return this.pagination.total_pages;
    },
    orderedLessons() {
      return _.orderBy(this.lessons, ["order", "id"]);
    },
    filteredEvidences() {
      var vm = this;
      let displayChosen = vm.displayType;

      let evidences = vm.evidences;
      return evidences

      // if (
      //   classChosen.id === 0 &&
      //   lessonChosen == undefined &&
      //   displayChosen.length == 0
      // ) {
      //   return evidences;
      // } else {
      //   var filtered = evidences.filter(function (e) {
      //     let filtered = true;

      //       if (displayChosen.length > 0) {
      //         filtered = e.media_type == displayChosen;
      //       }
          

      //     return filtered;
      //   });

      //   return filtered;
     // }
    },
    criteriaList() {
      if (this.criteria.length > 0) {
        return this.criteria;
      }
    },
  },
  data: function () {
    return {
      errored: false,
      loading: true,
      evidences: [],
      folder: "",

      evidenceAsset: {},
      evidenceMediatype: "",
      displayType: "",

      selectedEvidences: [],
      showMove: false,

      showAdditionWindow: false,

      inSelectMode: false,

      pageSize: 24,
      page: 1,
      currentPage: 1,
      pagination: {},

      filterValue: {},
      search: "",
    };
  },
  created() {
    this.folder = this.wordNumericValue(this.currentUser.schoolID);
  },
  mounted() {
    this.fetchEvidence();
  },
  watch: {
    lesson(newValue) {
     this.lessonSelect(newValue)
    },
    selectedClass(newValue) {
      this.showMore({page: 1, size: this.pageSize})
    }
  },
  methods: {
    loadFile(path) {
      return this.folder + "/" + path;
    },
    showEvidenceAsset(item) {
      this.evidenceAsset = item;
      this.$router.push({
        name: "EvidenceView",
        params: { evidence: item,
          readonly: false,
          course: this.course,
          closePath: "/unit-view" },
      });
    },
    lazyCaller(time = 500) {
      this.filterValue.tags = this.search;
      let vm = this;
      if (vm.search.length >= 3) {
        clearTimeout(vm.timeout);
        vm.timeout = setTimeout(() => {
          vm.showMore({page: 1, size: this.pageSize});
        }, time);
      }
    },
    fetchEvidence() {
      let searchType = this.hasValidLessonSelected ? "lessons" : "courses"
      let objectId = this.hasValidLessonSelected ? this.lesson.id : this.course.id
      EvidenceService.getByUnitOrLesson(searchType, objectId, this.selectedClass, this.page, this.pageSize)
        .then((response) => this.buildEvidences(response.data))
        .catch((err) => {
          console.log("evidences data error " + err);
          this.errored = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildEvidences(data) {
      this.evidences = data.data;
      this.pagination = data.meta.pagination;
    },
    lessonSelect(value) {
      this.lesson = value;
      this.selectedEvidences = [];
      this.showMore({page: 1, size: this.pageSize})
    },
    hasNotes(evidence) {
      return evidence.notes != undefined && evidence.notes.length > 0;
    },
    setDisplayType(value) {
      this.displayType = value;
      this.selectedEvidences = [];
    },
    getLessonName(evidence) {
      let value = this.lessons.find((l) => evidence.lesson_id === l.id);
      return value != undefined ? value.name : "";
    },
    getClassName(evidence) {
      return evidence.inclass.length > 1
        ? "<span>" + evidence.inclass + "</span>"
        : '<span class="text-red-600 py-1 rounded"><i class="fas fa-exclamation-circle pr-2"></i>Class not set</span>';
    },
    selectEvidence(evidence) {
      if (this.inSelectMode === false) {
        this.showEvidenceAsset(evidence)
      } else {
        if (
        this.selectedEvidences.some((e) => e.identifier === evidence.identifier)
      ) {
        this.selectedEvidences.splice(
          this.selectedEvidences.findIndex(
            (item) => item.identifier === evidence.identifier
          ),
          1
        );
      } else {
        this.selectedEvidences.push(evidence);
      }
      }

    },
    evidenceIsSelected(evidence) {
      let found = this.selectedEvidences.find(
        (e) => e.identifier === evidence.identifier
      );
      return found == undefined ? false : true;
    },
    closeMoveModal() {
      this.showMove = false;
      this.toggleSelect()
    },
    closeAddition() {
      this.showAdditionWindow = false;
    },
    closeView() {
      this.evidenceAsset = {}
    },
    showMore({page, size}) {
      this.page = page;
      this.currentPage = page;
      this.pageSize = size
      this.fetchEvidence();
      window.scrollTo(0,0);
    },
    toggleSelect() {
      this.inSelectMode = !this.inSelectMode
      this.selectedEvidences = [];
    },
    evidenceMoved() {
      this.closeMoveModal()
      this.showMore({page: 1, size: this.pageSize})
    },
    evidenceAdded() {
      this.closeAddition()
      this.showMore({page: 1, size: this.pageSize})
    }
  },
  components: {
    evidenceimage,
    evidencevideo,
    evidenceaddition,
    Pagination,
    ModalSlide,
    EvidenceMove,
      UpgradeContent,
  },
};
</script>

<style scoped>
</style>
